import React from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../../dashboardTokens';
import {
  OBSTACLE_COLOR, OPPORTUNITY_COLOR, SUMMARY_TEXT, ClampedText,
} from './summariesShared';

// Esteet ja mahdollisuudet (design guidance, specs/workshop-dashboard.md):
// red separates challenges from green opportunities. Tile: a split bar per
// lens. Expanded: the lens × pivot grid, each cell a pair of blobs sized by
// its note count.

const LensRows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(6px, 0.9vmin, 22px);
`;

const LensRow = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
  align-items: center;
  gap: clamp(8px, 1vmin, 24px);
  font-size: ${SUMMARY_TEXT};

  .name {
    color: ${({ color }) => color || DASH_COLORS.text};
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .counts {
    font-family: ${DASH_FONTS.numeric};
    font-size: ${DASH_TYPE.meta};
    white-space: nowrap;
    b { color: ${OBSTACLE_COLOR}; font-weight: 700; }
    i { color: ${OPPORTUNITY_COLOR}; font-style: normal; font-weight: 700; }
  }
`;

const SplitBar = styled.div`
  display: flex;
  height: clamp(10px, 1.2vmin, 28px);
  border-radius: 999px;
  overflow: hidden;
  background: ${DASH_COLORS.track};

  .obstacles { background: ${OBSTACLE_COLOR}; }
  .opportunities { background: ${OPPORTUNITY_COLOR}; }
`;

function LensesTile({ summary }) {
  const max = Math.max(1, ...summary.breakdown.map((l) => l.count));
  return (
    <LensRows>
      {summary.breakdown.map((lens) => (
        <LensRow key={lens.key} color={lens.color}>
          <span className="name">{lens.label}</span>
          <SplitBar>
            <span className="obstacles" style={{ width: `${(lens.obstacles / max) * 100}%` }} />
            <span className="opportunities" style={{ width: `${(lens.opportunities / max) * 100}%` }} />
          </SplitBar>
          <span className="counts"><b>{lens.obstacles}</b> / <i>{lens.opportunities}</i></span>
        </LensRow>
      ))}
    </LensRows>
  );
}

const GridTable = styled.table`
  border-collapse: collapse;
  width: 100%;

  th {
    font-size: ${DASH_TYPE.meta};
    font-weight: 700;
    padding: clamp(6px, 0.8vmin, 16px);
    text-align: center;
    vertical-align: bottom;
  }
  th.pivot {
    text-align: left;
    font-weight: 600;
    color: ${DASH_COLORS.text};
    max-width: clamp(160px, 20vw, 520px);
  }
  th.pivot .row {
    display: flex;
    align-items: center;
    gap: clamp(6px, 0.8vmin, 16px);
  }
  td {
    padding: clamp(4px, 0.6vmin, 14px);
    border-top: 1px solid ${DASH_COLORS.tileBorder};
    text-align: center;
    vertical-align: middle;
  }
`;

const InfoButton = styled.button`
  appearance: none;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${DASH_COLORS.accent};
  cursor: pointer;

  &:hover { background: ${DASH_COLORS.accentSoft}; }
  &:focus-visible { outline: 3px solid ${DASH_COLORS.accent}; outline-offset: 2px; }
`;

const BlobPair = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;

  i {
    border-radius: 50%;
    display: inline-block;
  }
`;

// Blob area ∝ note count → radius ∝ √count; the largest cell caps the scale.
function Blobs({ cell, maxCount }) {
  const maxPx = 34;
  const px = (count) => (count > 0 ? Math.max(8, Math.sqrt(count / maxCount) * maxPx) : 0);
  return (
    <BlobPair>
      {cell?.obstacles > 0 && (
        <i style={{ width: px(cell.obstacles), height: px(cell.obstacles), background: OBSTACLE_COLOR }} />
      )}
      {cell?.opportunities > 0 && (
        <i style={{ width: px(cell.opportunities), height: px(cell.opportunities), background: OPPORTUNITY_COLOR }} />
      )}
    </BlobPair>
  );
}

function LensesDetail({ t, summary }) {
  const navigate = useNavigate();
  const { grid, breakdown } = summary;
  const cellsByKey = new Map(grid.cells.map((c) => [`${c.lensKey}:${c.pivotKey}`, c]));
  const maxCount = Math.max(1, ...grid.cells.map((c) => Math.max(c.obstacles, c.opportunities)));

  // Deep link into the pivot's Yhteenveto on /lenses (direct URL added for
  // this, LensesScreen summaryDeepLink); ?kid etc. ride along.
  const openPivotSummary = (pivotKey) => {
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'pivot_detail_summary');
    params.set('pivot', pivotKey);
    navigate(`/lenses?${params.toString()}`);
  };

  return (
    <GridTable>
      <thead>
        <tr>
          <th className="pivot" aria-hidden="true" />
          {breakdown.map((lens) => (
            <th key={lens.key} style={{ color: lens.color || undefined }}>{lens.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {grid.pivots.map((pivot) => (
          <tr key={pivot.key}>
            <th className="pivot" scope="row">
              <span className="row">
                <ClampedText lines={2}>{pivot.label}</ClampedText>
                <InfoButton
                  type="button"
                  onClick={() => openPivotSummary(pivot.key)}
                  title={t('dashboard.openPivotSummary')}
                  aria-label={t('dashboard.openPivotSummary')}
                >
                  <Info size={18} />
                </InfoButton>
              </span>
            </th>
            {breakdown.map((lens) => (
              <td key={lens.key}>
                <Blobs cell={cellsByKey.get(`${lens.key}:${pivot.key}`)} maxCount={maxCount} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </GridTable>
  );
}

export const lensesView = { Tile: LensesTile, Detail: LensesDetail };
